import { html, css } from "lit";
import { customElement } from "lit/decorators.js";
import { map } from "lit/directives/map.js";
import { BaseVisualization, ReportEvent } from "./baseVisualization";
import { ContextData, LinkItem, LinksViewOptions } from "@reporting/rp-types/analytics";
import { ReportContext } from "@reporting/rp-types/analytics";
import { ModuleCode } from "@reporting/rp-types/accounting";
import { getContextIcon } from "../../adapters/contextBrowserAdapter";
import { getModuleDefinition, openReport } from "../../actions";

/**
 * List of links to other saved views. Not connected to the data model,
 * the links live in context view_options.items. The readonly attributes
 * (module, context_view, name, access) are appended by the server, links
 * to views the user cannot open are hidden (the editor still lists them).
 */
@customElement('rp-visual-links')
export class LinksVisualization extends BaseVisualization<ContextData> {

	static styles = css`
		:host {
			display: block;
			width: 100%;
			height: 100%;
			position: relative;
		}

		og-container {
			width: 100%;
			height: 100%;
		}

		.body {
			display: flex;
			flex-direction: column;
			gap: 4px;
			padding: 4px;
		}

		.item {
			display: flex;
			flex-direction: row;
			align-items: center;
			gap: 5px;
			flex: 0 0 22px;
			padding: 0 5px;
			border: 1px solid #6D6D6D;
			background-color: var(--og-colortype-gray-background-color-soft-40);
			border-radius: var(--og-base-radius);
			color: var(--og-text-color-3);
			cursor: pointer;
		}

		.item og-icon {
			color: #C8C8C8;
		}

		.item:hover {
			border: 1px solid var(--og-colortype-accent-border-color);
			background-color: var(--og-accent-color-alpha-30);
		}

		.item-name {
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
		}
	`;

	get viewOptions(): LinksViewOptions {
		return (this.contextData?.view_options ?? {}) as LinksViewOptions;
	}

	get visibleItems(): LinkItem[] {
		return (this.viewOptions.items ?? []).filter(link => link.access !== false);
	}

	getLinkIcon(link: LinkItem) {
		return getContextIcon(
			{context_view: link.context_view, module: link.module} as ReportContext,
			getModuleDefinition(link.module as ModuleCode)
		);
	}

	getLinkLabel(link: LinkItem) {
		return link.label || link.name || `#${link.id_context}`;
	}

	openLink(link: LinkItem) {
		if (!link.id_context) return;
		openReport(link.id_context, {target: 'new_tab', title: link.name});
	}

	refresh(contextData?: ContextData) {
		super.refresh(contextData);
		this.requestUpdate();
	}

	firstUpdated() {
		this.dispatchEvent(new ReportEvent('load', {visualization: this}));
	}

	render = () => html`
		<og-container>
			<div class="body" slot="content">
				${map(this.visibleItems, link => html`
					<div class="item" @click="${() => this.openLink(link)}">
						<og-icon .icon="${this.getLinkIcon(link)}"></og-icon>
						<div class="item-name">${this.getLinkLabel(link)}</div>
					</div>
				`)}
			</div>
		</og-container>
	`;

	layout() { }

}
